import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "str_cookie_consent";

// Fire Meta Pixel PageView + grant consent. Called only when the user opts in.
function grantConsent() {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("consent", "grant");
    window.fbq("track", "PageView");
  } catch (_) { /* ignore */ }
}

function revokeConsent() {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try { window.fbq("consent", "revoke"); } catch (_) { /* ignore */ }
}

export function openCookiePreferences() {
  // Clear the stored decision so the banner reappears
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  window.dispatchEvent(new CustomEvent("str:cookie-consent-reopen"));
}

export function CookieConsent() {
  const [state, setState] = useState("hidden"); // hidden | prompt

  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (_) {}
    if (saved === "accepted") { grantConsent(); return; }
    if (saved === "rejected") { revokeConsent(); return; }
    // No decision yet — show prompt
    revokeConsent();
    // Small delay so it doesn't flash on first paint
    const t = setTimeout(() => setState("prompt"), 600);
    const onReopen = () => setState("prompt");
    window.addEventListener("str:cookie-consent-reopen", onReopen);
    return () => { clearTimeout(t); window.removeEventListener("str:cookie-consent-reopen", onReopen); };
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch (_) {}
    grantConsent();
    setState("hidden");
  };
  const reject = () => {
    try { localStorage.setItem(STORAGE_KEY, "rejected"); } catch (_) {}
    revokeConsent();
    setState("hidden");
  };

  if (state !== "prompt") return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4"
    >
      <div className="max-w-3xl mx-auto border-2 border-ink bg-paper shadow-hard p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Cookie className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 id="cookie-consent-title" className="font-heading font-black text-base sm:text-lg tracking-tighter uppercase">
              Cookie preferences
            </h2>
            <p className="font-mono text-xs sm:text-[13px] text-ink/80 mt-2 leading-relaxed">
              We use strictly-necessary cookies to run the cart and login (always on). We'd also like to use{" "}
              <strong>analytics &amp; marketing cookies</strong> set by our analytics partners to measure page views,
              understand which products people love, and show you relevant ads on social platforms. These{" "}
              <strong>don't load unless you accept</strong>. See our{" "}
              <Link to="/privacy" className="underline">Privacy Policy</Link> for full details.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                data-testid="cookie-accept"
                onClick={accept}
                className="bg-ink text-paper h-10 px-5 font-mono text-xs uppercase tracking-widest hover:translate-x-0.5 hover:translate-y-0.5 transition-transform"
              >
                Accept all
              </button>
              <button
                data-testid="cookie-reject"
                onClick={reject}
                className="border-2 border-ink h-10 px-5 font-mono text-xs uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
              >
                Reject non-essential
              </button>
              <Link
                to="/privacy"
                data-testid="cookie-learn-more"
                className="h-10 px-2 flex items-center font-mono text-[11px] uppercase tracking-widest text-subtle hover:text-ink transition-colors"
              >
                Learn more →
              </Link>
            </div>
          </div>
          <button
            data-testid="cookie-close"
            aria-label="Close cookie banner"
            onClick={reject}
            className="p-1 -mt-1 -mr-1 hover:bg-surface transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
